import { InputTextField } from '@abbott-components/core';
import JSONSchema7Properties from '../../Shared/jSONSchema7Properties';
import Stack from '@mui/material/Stack';
import ControlState from '../../Shared/controlState';
import { ChangeEvent, useContext, useEffect, useRef, useState } from 'react';
import { WorkflowEngineContext } from '../../WorkflowEngine/workflowEngineReducer';
import { JSONSchema7 } from 'json-schema';
import Button from '@mui/material/Button';
import { useFormContext } from 'react-hook-form';
import Typography from '@mui/material/Typography';
import ImagePreview from './ImagePreview';
import getImageDimensionsAsync from './utilities/getImageDimensionsAsync';
import convertFileToDataURLAsync from '../../Shared/convertFileToDataURLAsync';
import Dimensions from './utilities/dimensions';
import { getAttachmentUrl } from '../../Shared/ajax';
import config from '../../Shared/config';
import formatName from '../formatName';
import {
  isValidFileSignature,
  isValidImageExtension
} from '../../Shared/fileTypeValidation';
import SizingSelect from '../../Shared/SizingSelect';

export default (control: ControlState) => {
  const { state } = useContext(WorkflowEngineContext);

  const { isReadOnlyView, versionExternalId } = state;

  const { schema } = control;

  const properties = schema?.properties as JSONSchema7Properties;

  const labelSchema = properties['label'] as JSONSchema7;
  const idSchema = properties['id'] as JSONSchema7;
  const externalIdSchema = properties['externalId'] as JSONSchema7;
  const dataSchema = properties['data'] as JSONSchema7;
  const filenameSchema = properties['filename'] as JSONSchema7;
  const mimeTypeSchema = properties['mimeType'] as JSONSchema7;
  const widthSchema = properties['width'] as JSONSchema7;
  const heightSchema = properties['height'] as JSONSchema7;
  const gridSizeSchema = properties['gridSize'] as JSONSchema7;

  const fieldLabel = formatName(labelSchema.$id as string);
  const fieldId = formatName(idSchema.$id as string);
  const fieldExternalId = formatName(externalIdSchema.$id as string);
  const fieldData = formatName(dataSchema.$id as string);
  const fieldFilename = formatName(filenameSchema.$id as string);
  const fieldMimeType = formatName(mimeTypeSchema.$id as string);
  const fieldWidth = formatName(widthSchema.$id as string);
  const fieldHeight = formatName(heightSchema.$id as string);
  const gridSize = formatName(gridSizeSchema.$id as string);

  const defaultWidth = widthSchema.default as number;
  const defaultHeight = heightSchema.default as number;

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const controlWrapperRef = useRef<HTMLDivElement | null>(null);

  const [fileDataURL, setFileDataURL] = useState<string>('');
  const [filename, setFilename] = useState<string>('');
  const [dimensions, setDimensions] = useState<Dimensions>({
    width: defaultWidth,
    height: defaultHeight
  });
  const [fileLimitError, setFileLimitError] = useState<string>('');
  const [fileSignatureError, setFileSignatureError] = useState<string>('');
  const [fileExtensionError, setFileExtensionError] = useState<string>('');

  const [maxWidth, setMaxWidth] = useState<number>(defaultWidth);

  // Ref. https://developer.mozilla.org/en-US/docs/Web/Media/Formats/Image_types
  const fileTypes = config.supportedImageMimeTypes;

  const formContext = useFormContext();

  const fieldFilenameState = formContext?.getFieldState(fieldFilename);

  const handleFileUploadChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const fileUpload: File | undefined =
      e.currentTarget.files && e.currentTarget.files.length
        ? (e.currentTarget.files[0] as File)
        : undefined;

    if (!fileUpload) return;

    const sizeInMB = fileUpload.size / 1000000;

    if (sizeInMB > config.imageSizeLimitinMB) {
      formContext?.clearErrors();
      setFileLimitError(
        `File must be no bigger than ${config.imageSizeLimitinMB} MB.`
      );
      return;
    }

    const validFileSignature = await isValidFileSignature(fileUpload);

    if (!validFileSignature) {
      setFileSignatureError(
        'File signature does not match supported file types.'
      );
      return;
    }

    if (!isValidImageExtension(fileUpload.name)) {
      setFileExtensionError('File extension is not of a supported file type.');
      return;
    }

    const imageDataURL = await convertFileToDataURLAsync(fileUpload);
    const imageDimensions = await getImageDimensionsAsync(imageDataURL);

    formContext?.setValue(fieldData, imageDataURL);
    formContext?.setValue(fieldFilename, fileUpload.name);
    formContext?.setValue(fieldMimeType, fileUpload.type);
    formContext?.setValue(fieldWidth, imageDimensions.width);
    formContext?.setValue(fieldHeight, imageDimensions.height);

    setMaxWidth(getImageResizeMaxWidth());
    setDimensions(imageDimensions);
    setFileDataURL(imageDataURL);
    setFilename(fileUpload.name);
    setFileLimitError('');
    setFileSignatureError('');
    setFileExtensionError('');

    formContext?.clearErrors();
  };

  const handleClearClick = () => {
    if (fileInputRef.current !== null) {
      fileInputRef.current.value = '';
    }

    formContext?.setValue(fieldData, '');
    formContext?.setValue(fieldFilename, '');
    formContext?.setValue(fieldMimeType, '');
    formContext?.setValue(fieldWidth, defaultWidth);
    formContext?.setValue(fieldHeight, defaultHeight);

    setDimensions({ width: defaultWidth, height: defaultHeight });
    setFileDataURL('');
    setFilename('');
    setFileLimitError('');
    setFileSignatureError('');
    setFileExtensionError('');

    formContext?.clearErrors();
  };

  const renderValidationErrors = () => {
    let errorMsg = '';

    if (fieldFilenameState?.error) {
      errorMsg = fieldFilenameState?.error?.message as string;
    }

    return errorMsg !== '' ? (
      <Typography sx={{ marginTop: 2, color: 'abbott.red' }}>
        {errorMsg}
      </Typography>
    ) : null;
  };

  const renderFileLimitError = () =>
    fileLimitError && (
      <Typography sx={{ marginTop: 2, color: 'abbott.red' }}>
        {fileLimitError}
      </Typography>
    );

  const renderFileSignatureError = () =>
    fileSignatureError && (
      <Typography sx={{ marginTop: 2, color: 'abbott.red' }}>
        {fileSignatureError}
      </Typography>
    );

  const renderFileExtensionError = () =>
    fileExtensionError && (
      <Typography sx={{ marginTop: 2, color: 'abbott.red' }}>
        {fileExtensionError}
      </Typography>
    );

  const renderHiddenField = () => (
    <Stack sx={{ display: 'none' }}>
      {/* Hidden field used to capture file upload value in form */}
      <InputTextField name={fieldData} />
      <InputTextField name={fieldExternalId} />
      <InputTextField name={fieldFilename} />
      <InputTextField name={fieldMimeType} />
      <InputTextField name={fieldWidth} />
      <InputTextField name={fieldHeight} />
    </Stack>
  );

  const getImageResizeMaxWidth = (): number => {
    if (controlWrapperRef.current !== null) {
      const controlWrapper = controlWrapperRef.current.closest(
        '[data-part="ControlWrapper"]'
      ) as HTMLDivElement;

      return controlWrapper.offsetWidth - 120;
    }

    return maxWidth;
  };

  const handleScaling = (width: number, height: number) => {
    setDimensions({ width, height });
    formContext?.setValue(fieldWidth, width);
    formContext?.setValue(fieldHeight, height);
  };

  useEffect(() => {
    const imageData = formContext?.getValues(fieldData);
    const imageDataURL = formContext?.getValues(fieldExternalId);
    const filename = formContext?.getValues(fieldFilename);
    const width = formContext?.getValues(fieldWidth);
    const height = formContext?.getValues(fieldHeight);
    const imageResizeMaxWidth = getImageResizeMaxWidth();
    const fileDataURL = imageDataURL
      ? getAttachmentUrl(versionExternalId, imageDataURL)
      : imageData;

    if (!imageDataURL) return;

    setMaxWidth(imageResizeMaxWidth);
    setDimensions({ width: Number(width), height: Number(height) });
    setFilename(filename);
    setFileDataURL(fileDataURL);
  }, []);

  return (
    <Stack ref={controlWrapperRef} direction="column" spacing={3}>
      {renderHiddenField()}
      <Stack direction="row" spacing={3}>
        <Stack sx={{ flexGrow: 2 }}>
          <InputTextField
            name={fieldLabel}
            label="Image Title"
            isDisabled={isReadOnlyView}
          />
        </Stack>
        <Stack sx={{ flexGrow: 1 }}>
          <InputTextField
            name={fieldId}
            label="ID"
            isDisabled={isReadOnlyView}
          />
        </Stack>
        <SizingSelect isReadOnlyView={isReadOnlyView} name={gridSize} />
      </Stack>
      <Stack direction="row" spacing={3}>
        <ImagePreview
          dataURL={fileDataURL}
          filename={filename}
          dimensions={dimensions}
          maxWidth={maxWidth || undefined}
          onScaling={handleScaling}
        />
      </Stack>
      <Stack direction="row" spacing={3}>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            component="label"
            disabled={isReadOnlyView}
          >
            {'Upload'}
            <input
              ref={fileInputRef}
              hidden
              accept={fileTypes.join(',')}
              type="file"
              onChange={handleFileUploadChange}
            />
          </Button>
          <Button
            variant="text"
            component="label"
            onClick={handleClearClick}
            disabled={isReadOnlyView}
          >
            {'Clear'}
          </Button>
        </Stack>
        {filename && (
          <Stack>
            <Typography variant="caption">{filename}</Typography>
          </Stack>
        )}
        {fileDataURL && (
          <Stack>
            <Typography variant="caption">{`${dimensions.width}x${dimensions.height} (pixels)`}</Typography>
          </Stack>
        )}
      </Stack>
      {renderValidationErrors()}
      {renderFileLimitError()}
      {renderFileSignatureError()}
      {renderFileExtensionError()}
    </Stack>
  );
};
